import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { Box } from '@gonfalon/observability-ui/components/Box';
import {
  IconSolidCheveronDown,
  IconSolidCheveronUp,
  IconSolidSearch,
} from '@gonfalon/observability-ui/components/icons';
import { Text } from '@gonfalon/observability-ui/components/Text';
import { Icon } from '@launchpad-ui/icons';

import { InvestigationRow } from '../../../conversation/InvestigationRow';

import {
  chevronIcon,
  hoveredOpacity,
  icon,
  innerContent,
  innerDivider,
  toolDetailsContent,
  toolDetailsIcon,
  toolDetailsPre,
  toolDetailsToggle,
} from '../../../../internal/components/AgentInsights/components/messages/ShimmerText.css';
import { copyHoverGroup } from '../../../../internal/components/conversation/ConversationChrome.css';

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(' ');

const LOADING_TEXTS = [
  'Thinking',
  'Observing',
  'Searching',
  'Fizzbuzzing',
  'Analyzing',
  'Investigating',
  'Pondering',
  'Computing',
  'Exploring',
  'Discovering',
];

function getCommandFromToolInputs(toolInputs: unknown): string | null {
  if (!toolInputs || typeof toolInputs !== 'object') {
    return null;
  }
  if (!('command' in toolInputs)) {
    return null;
  }
  const { command } = toolInputs;
  return typeof command === 'string' ? command : null;
}

/**
 * ShimmerText component - displays loading animation and expandable tool results.
 * Accepts a `renderContent` thunk that is only invoked when the panel is expanded,
 * so callers can avoid expensive parsing/rendering for collapsed tools.
 */
export const ShimmerText: FC<{
  text: string;
  isLoading?: boolean;
  toolInputs?: unknown;
  onClick?: () => void;
  renderContent?: () => ReactNode;
  startExpanded?: boolean;
}> = ({ text, isLoading = false, toolInputs, onClick, renderContent, startExpanded = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(startExpanded);
  const [showToolDetails, setShowToolDetails] = useState(false);
  const [rotatingText, setRotatingText] = useState(text);

  // One-directional sync: when startExpanded flips to true (e.g. content arrives
  // mid-mount and the parent's auto-expand check now passes), open the panel.
  // Never force-closes — preserves the user's ability to manually collapse.
  const [seenStartExpanded, setSeenStartExpanded] = useState(startExpanded);
  if (startExpanded !== seenStartExpanded) {
    setSeenStartExpanded(startExpanded);
    if (startExpanded) {
      setIsExpanded(true);
    }
  }

  // Bash/command tools with no output: show command only, not expandable
  const commandFromInputs = getCommandFromToolInputs(toolInputs);
  const hasContent = !!renderContent;
  const isCommandWithNoOutput = !!commandFromInputs && !hasContent;
  const isInteractive = hasContent && !isCommandWithNoOutput;
  const showExpandedContent = isExpanded && isInteractive;
  const isThinking = text === 'Thinking...';
  const thinking = isLoading && isThinking;
  const [wasThinking, setWasThinking] = useState(thinking);
  if (thinking !== wasThinking) {
    setWasThinking(thinking);
    if (thinking) {
      setRotatingText(text);
    }
  }

  // Rotate text every 2 seconds when loading and text is "Thinking..."
  useEffect(() => {
    if (!thinking) {
      return;
    }
    let currentIndex = 0;
    const interval = setInterval(() => {
      currentIndex = (currentIndex + 1) % LOADING_TEXTS.length;
      setRotatingText(`${LOADING_TEXTS[currentIndex]}...`);
    }, 2000);

    return () => clearInterval(interval);
  }, [thinking]);

  const handleToggle = () => {
    if (isInteractive) {
      setIsExpanded(!isExpanded);
      onClick?.();
    }
  };

  const rowIcon = (() => {
    if (isThinking) {
      return <IconSolidSearch className={icon} />;
    }
    // Command/bash tools use the semantic terminal icon (matches Figma / Read ToolCard).
    // Expandability is the header toggle (`aria-expanded`), not a chevron.
    if (commandFromInputs) {
      return <Icon name="terminal" size="small" />;
    }
    if (isInteractive) {
      const chevronClassName = cx(chevronIcon, isHovered && hoveredOpacity);
      return isExpanded ? (
        <IconSolidCheveronDown className={chevronClassName} />
      ) : (
        <IconSolidCheveronUp className={chevronClassName} />
      );
    }
    return <Icon name="search" size="small" />;
  })();

  return (
    <div onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <InvestigationRow
        className={copyHoverGroup}
        variant="chat"
        icon={rowIcon}
        label={thinking ? rotatingText : text}
        detail={isCommandWithNoOutput && commandFromInputs ? commandFromInputs : undefined}
        isLoading={isLoading}
        expanded={showExpandedContent}
        onToggle={isInteractive ? handleToggle : undefined}
      >
        {showExpandedContent && renderContent ? (
          <Box px="8" pb="8" pt="8">
            <Box cssClass={innerContent}>{renderContent()}</Box>

            {toolInputs ? (
              <>
                <Box cssClass={innerDivider} />
                <Box
                  display="inline-flex"
                  alignItems="center"
                  gap="4"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setShowToolDetails(!showToolDetails);
                  }}
                  cssClass={toolDetailsToggle}
                >
                  <Icon name="code-circle-outline" size="small" className={toolDetailsIcon} />
                  <Text size="small" color="weak" weight="medium">
                    <Message
                      id={showToolDetails ? 'vega.conversation.hideToolInputs' : 'vega.conversation.showToolInputs'}
                    />
                  </Text>
                </Box>
                {showToolDetails && (
                  <Box marginTop="8" cssClass={toolDetailsContent}>
                    <pre className={toolDetailsPre}>{JSON.stringify(toolInputs, null, 2)}</pre>
                  </Box>
                )}
              </>
            ) : null}
          </Box>
        ) : null}
      </InvestigationRow>
    </div>
  );
};
